import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Avatar,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface TenantUserDetail {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  tenant_name: string;
  tenant_subdomain: string;
  permissions: Record<string, any>;
  profile_id: string;
  user_id: string;
}

function TenantUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<TenantUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUserDetail();
  }, [id]);

  const loadUserDetail = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // テナントユーザー詳細を取得（APIエンドポイントを後で実装）
      const response = await apiService.getTenantUserDetail(id) as any;
      
      if (response?.success && response?.data) {
        setUser(response.data);
      } else {
        setError('ユーザー情報の取得に失敗しました');
      }
    } catch (error: any) {
      console.error('Failed to load user detail:', error);
      setError(error.message || 'ユーザー情報の取得中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'manager': return 'マネージャー';
      case 'viewer': return '閲覧者';
      case 'user': return 'ユーザー';
      default: return role;
    }
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    switch (role) {
      case 'admin': return 'primary';
      case 'manager': return 'secondary';
      default: return 'default';
    }
  };

  const handleGoToTenant = () => {
    if (user?.tenant_id) {
      navigate(`/tenants/${user.tenant_id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tenant-users')}
            sx={{ mr: 2 }}
          >
            戻る
          </Button>
          <Typography variant="h4">テナントユーザー詳細</Typography>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tenant-users')}
            sx={{ mr: 2 }}
          >
            戻る
          </Button>
          <Typography variant="h4">ユーザーが見つかりません</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenant-users')}
          sx={{ mr: 2 }}
        >
          戻る
        </Button>
        <Typography variant="h4">テナントユーザー詳細</Typography>
      </Box>

      <Grid container spacing={3}>
        {/* ユーザー基本情報 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ width: 64, height: 64, mr: 2 }}>
                  <PersonIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6">{user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ステータス
                </Typography>
                <Chip
                  label={user.is_active ? 'アクティブ' : '非アクティブ'}
                  color={user.is_active ? 'success' : 'error'}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  ロール
                </Typography>
                <Chip
                  label={getRoleLabel(user.role)}
                  color={getRoleColor(user.role)}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 詳細情報 */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* 連絡先情報 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  連絡先情報
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          メールアドレス
                        </Typography>
                        <Typography variant="body1">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  {user.phone && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            電話番号
                          </Typography>
                          <Typography variant="body1">
                            {user.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>

            {/* テナント情報 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    所属テナント
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleGoToTenant}
                    startIcon={<BusinessIcon />}
                  >
                    テナント詳細へ
                  </Button>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      テナント名
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {user.tenant_name}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      サブドメイン
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {user.tenant_subdomain}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* システム情報 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  システム情報
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          作成日
                        </Typography>
                        <Typography variant="body1">
                          {new Date(user.created_at).toLocaleString('ja-JP')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ScheduleIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          最終ログイン
                        </Typography>
                        <Typography variant="body1">
                          {user.last_login_at 
                            ? new Date(user.last_login_at).toLocaleString('ja-JP')
                            : '未ログイン'
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          ユーザーID
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {user.id}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TenantUserDetail;